import {
  Box,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  type SelectProps,
  type SelectChangeEvent,
  type PopoverOrigin,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ReactComponent as DownArrowIcon } from 'assets/downArrowIcon.svg'
import React, { type ReactElement } from 'react'
import { type SxProps } from '@mui/system'

import styles from './SimpleDropdown.module.scss'

import type { SelectOption } from 'common/utils/types'

interface SimpleDropdownProps extends SelectProps {
  name?: string
  options: SelectOption[]
  selectedOption?: string
  placeholder?: string
  handleChange: (value: string) => void
  typographySx?: SxProps
  transformOrigin?: PopoverOrigin
  anchorOrigin?: PopoverOrigin
}

const SimpleDropdown: React.FC<SimpleDropdownProps> = ({
  name,
  options,
  selectedOption,
  handleChange,
  typographySx,
  placeholder,
  transformOrigin = { vertical: 'top', horizontal: 'left' },
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  ...rest
}): ReactElement => {
  const { palette } = useTheme()
  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <InputLabel>
        <Typography variant="title3">{name ?? ''}</Typography>
      </InputLabel>
      <FormControl fullWidth>
        <Select
          fullWidth
          className={styles.filter}
          {...rest}
          MenuProps={{
            autoFocus: false,
            sx: {
              zIndex: 2000,
              '& .MuiMenu-root': {
                backgroundColor: palette.blue800.main,
                padding: 0,
              },
              '& .MuiMenu-paper': {
                borderBottom: 'solid 13px',
                borderTop: 'solid 13px',
                borderColor: palette.blue800.main,
                backgroundColor: palette.blue800.main,
              },
              '& .MuiMenu-list': {
                width: 'auto',
                padding: 0,
                maxHeight: '300px',
                backgroundColor: palette.blue800.main,
                overflow: 'auto',
              },
            },
            marginThreshold: 0,
            anchorOrigin,
            transformOrigin,
          }}
          IconComponent={DownArrowIcon}
          value={selectedOption}
          defaultValue=""
          onChange={(event: SelectChangeEvent<string>) => {
            handleChange(event.target.value)
          }}
          displayEmpty
          input={<OutlinedInput />}
          renderValue={() => (
            <Typography
              className={styles.selectedOptionLabel}
              variant="subtitle1"
              color={selectedOption ? palette.blue100.main : palette.gray.main}
            >
              {options?.find((o) => o.id === selectedOption)?.name ?? placeholder}
            </Typography>
          )}
        >
          {options?.map((option: SelectOption) => (
            <MenuItem
              key={option.id}
              value={option.id}
              sx={{
                height: '31px',
                '&.Mui-selected': {
                  backgroundColor: palette.blue900.main,
                  color: palette.charts.purple100.main,
                },
                '&:hover': {
                  backgroundColor: palette.blue900.main,
                  color: palette.charts.purple100.main,
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography sx={typographySx} variant="subtitle2">
                    {option.name}
                  </Typography>
                }
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export default SimpleDropdown
